import { useEffect, useState } from 'react';
import Marquee from 'react-fast-marquee';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { QuizFullLogo } from '@/components/QuizFullLogo';
import { useEffectOnce, useViewportDevice } from '@/hooks';
import { ChevronRightIcon } from '@/icons';
import { trackClassroomEvent } from '@/shared/features/classroom/utils';
import { normalizeEntryCode } from '@/shared/utils';
import { Button } from '@/ui_v3';
import { cn } from '@/utils';
import { EnterCodeForm, JOIN_CODE_MAX_LENGTH } from './components';
import { useJoinFlow } from './hooks';
const JoinPageBackground = () => {
  const {
    isMobile
  } = useViewportDevice();
  return <div className="pointer-events-none size-full" data-sentry-component="JoinPageBackground" data-sentry-source-file="JoinPageView.tsx">
      <Marquee className="h-full" speed={50} autoFill direction="left" gradient={false} data-sentry-element="Marquee" data-sentry-source-file="JoinPageView.tsx">
        <Image src="/assets/entercode_back_logo.png" alt="Enter Code Page Logo" className={cn('object-contain opacity-[0.12] h-dvh py-[70px]', isMobile && 'py-[20px]')} fetchPriority="high" priority width={734} height={760} style={{
        width: 'auto'
      }} data-sentry-element="Image" data-sentry-source-file="JoinPageView.tsx" />
        <Image src="/assets/entercode_back_light.png" className={cn('object-contain h-dvh py-[70px]', isMobile && 'py-[20px]')} alt="Enter Code Page light" fetchPriority="high" priority width={508} height={760} style={{
        width: 'auto'
      }} data-sentry-element="Image" data-sentry-source-file="JoinPageView.tsx" />
      </Marquee>
    </div>;
};
export function JoinPageView() {
  const router = useRouter();
  const {
    t
  } = useTranslation(['common']);
  const [codeValue, setCodeValue] = useState('');
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const {
    errorMessage,
    isAuthInitialized,
    isSubmitting,
    startJoinFlow
  } = useJoinFlow();
  const handleToPublicButtonClick = async () => {
    void router.push('/public');
    logger.track('exit_entercode');
  };
  useEffectOnce(() => {
    logger.track('enter_entercode');
    trackClassroomEvent('view_join_code_page', {
      source: 'join_page',
      hasPrefilledCode: typeof router.query.code === 'string'
    });
  });
  useEffect(() => {
    if (!router.isReady || typeof router.query.code !== 'string') return;
    // 수동 입력(EnterCodeForm)과 동일한 정규화 — U-prefix 유저코드를 보존해야
    // 프리필/딥링크(/join?code=U...) 자동제출도 useJoinFlow 의 U 분기로 /code-login 에 넘어간다.
    setCodeValue(normalizeEntryCode(router.query.code, JOIN_CODE_MAX_LENGTH));
  }, [router.isReady, router.query.code]);
  useEffect(() => {
    if (hasAutoSubmitted) return;
    if (!router.isReady || typeof router.query.code !== 'string') return;
    if (!codeValue) return;
    if (!isAuthInitialized) return;
    setHasAutoSubmitted(true);
    void startJoinFlow({
      code: codeValue
    });
  }, [router.isReady, router.query.code, codeValue, hasAutoSubmitted, isAuthInitialized, startJoinFlow]);
  return <div className="pointer-events-auto fixed flex h-dvh w-full justify-center overscroll-none bg-gradient-to-r from-blue-300 to-mint-400" data-sentry-component="JoinPageView" data-sentry-source-file="JoinPageView.tsx">
      <div className="absolute left-0 top-0 flex size-full items-center justify-center px-md">
        <EnterCodeForm codeValue={codeValue} errorMessage={errorMessage} isSubmitting={isSubmitting} onCodeChange={setCodeValue} onSubmit={() => void startJoinFlow({
        code: codeValue
      })} data-sentry-element="EnterCodeForm" data-sentry-source-file="JoinPageView.tsx" />
      </div>
      <Button className="fixed bottom-[42px] z-[500] cursor-pointer bg-opacity-white-8 hover:bg-opacity-white-10 active:bg-opacity-white-10" variant="contained" intent="neutral" rounded="full" size="md" leftSlot={<QuizFullLogo className="h-[20px]" />} onClick={handleToPublicButtonClick} rightSlot={<ChevronRightIcon className="size-4" />} data-sentry-element="Button" data-sentry-source-file="JoinPageView.tsx">
        {t('common:header.explore.button')}
      </Button>
      <JoinPageBackground data-sentry-element="JoinPageBackground" data-sentry-source-file="JoinPageView.tsx" />
    </div>;
}